import type { UserRole } from "@/types/auth.types";

const ROLE_ALIASES: Record<string, UserRole> = {
  USER: "user",
  CONTRIBUTOR: "user",
  COLLECTOR: "collector",
  COLLECTEUR: "collector",
  ANNOTATOR: "collector",
  ANNOTATEUR: "collector",
  MODERATOR: "moderator",
  ADMIN: "admin",
  ACCOUNTANT: "accountant",
  COMPTABLE: "accountant",
  SUPER_ADMIN: "super_admin",
};

export function normalizeUserRole(value: unknown): UserRole {
  if (typeof value !== "string") return "user";

  const normalized = value.trim();
  if (!normalized) return "user";

  const lowerCaseRole = normalized.toLowerCase() as UserRole;
  if (["user", "collector", "moderator", "admin", "accountant", "super_admin"].includes(lowerCaseRole)) {
    return lowerCaseRole;
  }

  return ROLE_ALIASES[normalized.toUpperCase()] ?? "user";
}

export function isAdminRole(role: unknown): boolean {
  const normalizedRole = normalizeUserRole(role);
  return normalizedRole === "admin" || normalizedRole === "super_admin";
}

export function canAccessContributorWorkspace(role: unknown): boolean {
  const normalizedRole = normalizeUserRole(role);
  return normalizedRole === "user" || normalizedRole === "collector";
}

export function canAccessModeratorWorkspace(role: unknown): boolean {
  return normalizeUserRole(role) === "moderator";
}

export function canModerateTests(role: unknown): boolean {
  const normalizedRole = normalizeUserRole(role);
  return normalizedRole === "moderator" || normalizedRole === "admin" || normalizedRole === "super_admin";
}

/** Where each profile lands after signing in: its own space. */
export function homePathForRole(role: unknown, lang: string): string {
  switch (normalizeUserRole(role)) {
    case "admin":
    case "super_admin":
      return `/${lang}/admin/dashboard`;
    case "moderator":
      return `/${lang}/moderator/todo`;
    case "accountant":
      return `/${lang}/accountant/dashboard`;
    case "collector":
      return `/${lang}/collector`;
    default:
      return `/${lang}`;
  }
}

/**
 * Where a page outside their space sends this profile: the account settings
 * profile of their own panel for the old /settings link, their home otherwise.
 */
export function ownSpaceRedirect(path: string, role: unknown, lang: string): string {
  const home = homePathForRole(role, lang);
  if (isBackOfficeRole(role) && /\/settings\/?$/.test(path.split("?")[0])) {
    return home.replace(/\/(dashboard|todo)$/, "/profile");
  }
  return home;
}

/** Admins, moderators and accountants: each works only in their own panel. */
export function isBackOfficeRole(role: unknown): boolean {
  const normalized = normalizeUserRole(role);
  return normalized === "admin" || normalized === "super_admin" || normalized === "moderator" || normalized === "accountant";
}

const SPACE_PREFIXES: Array<{ prefix: string; roles: UserRole[] }> = [
  { prefix: "/admin", roles: ["admin", "super_admin"] },
  { prefix: "/moderator", roles: ["moderator"] },
  { prefix: "/accountant", roles: ["accountant"] },
  { prefix: "/collector", roles: ["collector"] },
];

/** Pages of the public website, open to every profile ("Site web"). */
const PUBLIC_PREFIXES = ["/site", "/leaderboard", "/data-policy", "/complaints"];
const CONTRIBUTION_PREFIXES = [
  "/speak-contribute",
  "/label-images",
  "/validate-audio",
  "/validate-transcription",
  "/transcription",
  "/transcription-phrase",
  "/initial-text",
  "/initial-audio",
  "/initial-transcription",
  "/initial-review",
];
const ACCOUNTANT_EXCLUDED_PREFIXES = ["/initial-transcription", "/initial-review"];

const matches = (rest: string, prefix: string) => rest === prefix || rest.startsWith(`${prefix}/`) || rest.startsWith(`${prefix}?`);

/**
 * Whether this profile may open `path`. Each panel belongs to its roles only
 * (an admin does not open the accountant or collector space), back-office
 * roles stay in their own shell when they open approved contribution pages. Also
 * used for a remembered page: after an admin signed out, a contributor
 * signing in on the same browser must not be sent to the admin space.
 */
export function canReturnTo(path: string, role: unknown, lang: string): boolean {
  const normalized = normalizeUserRole(role);
  const rest = (path.startsWith(`/${lang}/`) ? path.slice(lang.length + 1) : path) || "/";
  const space = SPACE_PREFIXES.find(({ prefix }) => matches(rest, prefix));
  if (space) return space.roles.includes(normalized);
  if (isBackOfficeRole(normalized)) {
    if (normalized === "accountant" && ACCOUNTANT_EXCLUDED_PREFIXES.some((prefix) => matches(rest, prefix))) {
      return false;
    }
    return [...PUBLIC_PREFIXES, ...CONTRIBUTION_PREFIXES].some((prefix) => matches(rest, prefix));
  }
  return true;
}
